import React, { useEffect, useMemo, useState } from 'react';
import {
  XMarkIcon, PaperAirplaneIcon, CheckCircleIcon, ExclamationTriangleIcon, ClipboardDocumentIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import quoteService from '../../services/quoteService';
import tariffService from '../../services/tariffService';
import { errText } from '../../services/scheduleService';
import TariffPicker from '../billing/TariffPicker';
import TariffLines, { Issue, rands } from '../billing/TariffLines';

export const QUOTE_STATUS = {
  draft: { label: 'Quote draft', tone: 'bg-slate-100 text-slate-600' },
  sent: { label: 'Quote sent', tone: 'bg-blue-100 text-blue-700' },
  expired: { label: 'Quote expired', tone: 'bg-amber-100 text-amber-800' },
  accepted: { label: 'Quote accepted', tone: 'bg-green-100 text-green-700' },
  declined: { label: 'Quote declined', tone: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Quote cancelled', tone: 'bg-slate-100 text-slate-500' },
};
const PROCEDURE_KINDS = ['surgical', 'anaesthetic_procedure', 'consultation'];
const fmtWhen = (d) => (d ? new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

// The editor's inputs from a saved quote.
const inputsFrom = (q) => ({
  codes: (q.practice_lines || []).filter((l) => PROCEDURE_KINDS.includes(l.kind) && l.code).map((l) => ({ code: l.code, description: l.description })),
  minutes: q.facts?.duration_minutes == null ? '' : String(q.facts.duration_minutes),
  bmi: q.facts?.bmi == null ? '' : String(q.facts.bmi),
  emergency: q.facts?.emergency === true,
  prone: q.facts?.prone === true,
  note: q.note || '',
});

/**
 * QuoteEditor — the anaesthetic estimate for one patient on one booking, and
 * sending it to them for informed financial consent. Opens on the latest quote
 * for that patient and booking: a draft to finish, or a sent / answered quote
 * to follow up (or replace with a new one).
 *   patient: { patient_id, name, email, payer, scheme_name, bmi? } — bmi from the pre-op questionnaire starts a new quote
 *   bookingLabel: "Mon 14 Sep · Milpark · Theatre 2 · Dr Pillay"
 *   onChanged(): the booking's quote status changed
 */
const QuoteEditor = ({ bid, patient, bookingLabel, plannedProcedure, onClose, onChanged }) => {
  const [quote, setQuote] = useState(null);
  const [mode, setMode] = useState('loading'); // loading | edit | view
  const [inputs, setInputs] = useState({ codes: [], minutes: '60', bmi: patient.bmi != null ? String(patient.bmi) : '', emergency: false, prone: false, note: '' });
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [email, setEmail] = useState(patient.email || '');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(null); // { link, emailed, email_error }
  const [showLines, setShowLines] = useState(false);
  const [copied, setCopied] = useState(false);

  // The latest quote for this patient on this booking.
  useEffect(() => {
    quoteService.list({ bid, patientId: patient.patient_id })
      .then(({ quotes }) => {
        const latest = quotes.sort((a, b) => b.quote_id - a.quote_id)[0] || null;
        setQuote(latest);
        if (latest) setInputs(inputsFrom(latest));
        setMode(!latest || latest.status === 'draft' ? 'edit' : 'view');
      })
      .catch((e) => { setErr(errText(e)); setMode('edit'); });
  }, [bid, patient.patient_id]);

  // Suggest codes from the planned procedure.
  useEffect(() => {
    if (mode !== 'edit' || inputs.codes.length || !plannedProcedure) { setSuggestions([]); return; }
    tariffService.search(plannedProcedure, { limit: 3 }).then(setSuggestions).catch(() => setSuggestions([]));
  }, [mode, plannedProcedure, inputs.codes.length]);

  const payload = useMemo(() => ({
    bid,
    patient_id: patient.patient_id,
    codes: inputs.codes.map((c) => c.code),
    facts: { duration_minutes: inputs.minutes === '' ? null : Number(inputs.minutes), bmi: inputs.bmi === '' ? null : Number(inputs.bmi), emergency: inputs.emergency, prone: inputs.prone },
    note: inputs.note,
    procedure_text: plannedProcedure || undefined,
  }), [bid, patient.patient_id, inputs, plannedProcedure]);

  // Live estimate while editing.
  useEffect(() => {
    if (mode !== 'edit') return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setEstimating(true);
      try {
        const e = await quoteService.preview(payload);
        if (!cancelled) { setEstimate(e); setErr(''); }
      } catch (e) {
        if (!cancelled) setErr(errText(e));
      } finally {
        if (!cancelled) setEstimating(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mode, payload]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const set = (k) => (e) => setInputs((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const addCode = (row) => setInputs((f) => (f.codes.some((c) => c.code === row.code) ? f : { ...f, codes: [...f.codes, { code: row.code, description: row.description }] }));

  const save = async () => {
    const out = quote && quote.status === 'draft' ? await quoteService.update(quote.quote_id, payload) : await quoteService.create(payload);
    setQuote(out.quote);
    onChanged?.();
    return out.quote;
  };

  const run = async (what, fn) => {
    setBusy(what);
    setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy('');
    }
  };

  const saveDraft = () => run('save', async () => { await save(); });
  const saveAndSend = () => run('send', async () => {
    const q = await save();
    const out = await quoteService.send(q.quote_id, email);
    setQuote(out.quote);
    setSent({ link: out.link, emailed: out.emailed, email_error: out.email_error });
    setMode('view');
    onChanged?.();
  });
  const resend = () => run('send', async () => {
    const out = await quoteService.send(quote.quote_id, email);
    setQuote(out.quote);
    setSent({ link: out.link, emailed: out.emailed, email_error: out.email_error });
    onChanged?.();
  });
  const cancel = () => run('cancel', async () => { setQuote(await quoteService.cancel(quote.quote_id)); onChanged?.(); });
  const startNew = () => { setQuote(null); setSent(null); setMode('edit'); };

  const copy = async () => {
    try { await navigator.clipboard.writeText(sent.link); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* select it by hand */ }
  };

  const errors = (estimate?.issues || []).filter((i) => i.level === 'error').length;
  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30';
  const s = quote ? QUOTE_STATUS[quote.status] : null;

  const Totals = ({ practice, scheme, shortfall, payer, schemeName }) => (
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="rounded-lg bg-slate-50 px-2 py-2.5">
        <div className="text-[11px] text-slate-500">Anaesthetic fee</div>
        <div className="text-lg font-bold text-slate-800">{rands(practice)}</div>
      </div>
      <div className="rounded-lg bg-slate-50 px-2 py-2.5">
        <div className="text-[11px] text-slate-500">{payer === 'private' ? 'Medical aid' : `${schemeName || 'Medical aid'} pays (est.)`}</div>
        <div className="text-lg font-bold text-slate-800">{payer === 'private' ? '—' : scheme == null ? '?' : rands(scheme)}</div>
      </div>
      <div className="rounded-lg bg-brand-deep px-2 py-2.5 text-white">
        <div className="text-[11px] text-teal-100">Patient pays (est.)</div>
        <div className="text-lg font-bold">{shortfall == null ? '?' : rands(shortfall)}</div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-brand-deep">Estimate for {patient.name}</h2>
            <p className="truncate text-xs text-slate-500">{bookingLabel}{plannedProcedure ? ` · ${plannedProcedure}` : ''}</p>
            <p className="text-xs text-slate-500">{patient.payer === 'private' ? 'Private — pays the account' : patient.scheme_name || 'Medical aid'}</p>
          </div>
          <div className="flex items-center gap-2">
            {s && <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${s.tone}`}>{quote.quote_no} · {s.label}</span>}
            <button onClick={onClose} disabled={!!busy} className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
          {mode === 'loading' && <p className="text-slate-400">Loading…</p>}

          {mode === 'view' && quote && (
            <>
              {sent && (
                <div className={`rounded-lg border px-3 py-2.5 ${sent.emailed ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                  <p className="flex items-center gap-1.5 font-semibold">
                    {sent.emailed ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
                    {sent.emailed ? `Emailed to ${quote.sent_to}.` : sent.email_error}
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <input readOnly value={sent.link} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-2 py-1 font-mono text-xs text-slate-700" />
                    <button onClick={copy} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      <ClipboardDocumentIcon className="h-4 w-4" /> {copied ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs">You can also send this link by WhatsApp or SMS. It works once, up to and including {new Date(new Date(quote.expires_at).getTime() - 1000).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}.</p>
                </div>
              )}

              <Totals practice={quote.practice_total} scheme={quote.scheme_total} shortfall={quote.shortfall} payer={quote.payer} schemeName={quote.scheme_name} />

              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-slate-200 px-3 py-2.5 text-xs">
                <div><dt className="text-slate-400">Sent</dt><dd className="font-semibold text-slate-700">{quote.sent_at ? `${fmtWhen(quote.sent_at)}${quote.sent_to ? ` to ${quote.sent_to}` : ''}` : 'Not yet'}</dd></div>
                <div><dt className="text-slate-400">Opened by the patient</dt><dd className="font-semibold text-slate-700">{quote.opened_at ? fmtWhen(quote.opened_at) : 'Not yet'}</dd></div>
                {quote.responded_at && (
                  <div className="col-span-2">
                    <dt className="text-slate-400">{quote.status === 'accepted' ? 'Accepted' : 'Declined'}</dt>
                    <dd className="font-semibold text-slate-700">
                      {fmtWhen(quote.responded_at)}{quote.response_name ? ` by ${quote.response_name}` : ''}
                      {quote.response_note && <span className="block font-normal text-slate-600">“{quote.response_note}”</span>}
                    </dd>
                  </div>
                )}
                <div><dt className="text-slate-400">Codes</dt><dd className="font-mono text-slate-700">{inputsFrom(quote).codes.map((c) => c.code).join(', ') || '—'}</dd></div>
                <div><dt className="text-slate-400">Expected time</dt><dd className="font-semibold text-slate-700">{quote.facts?.duration_minutes != null ? `${quote.facts.duration_minutes} min` : '—'}</dd></div>
              </dl>

              <div className="flex flex-wrap justify-end gap-2">
                {['sent', 'expired'].includes(quote.status) && (
                  <button onClick={cancel} disabled={!!busy} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">{busy === 'cancel' ? 'Cancelling…' : 'Cancel quote'}</button>
                )}
                <button onClick={startNew} disabled={!!busy} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Make a new quote</button>
                {['sent', 'expired'].includes(quote.status) && (
                  <button onClick={resend} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-60">
                    <PaperAirplaneIcon className="h-4 w-4" /> {busy === 'send' ? 'Sending…' : 'Send a new link'}
                  </button>
                )}
              </div>
            </>
          )}

          {mode === 'edit' && (
            <>
              <div>
                <div className="mb-1 text-xs font-semibold text-slate-600">Planned procedure codes</div>
                <TariffPicker onPick={addCode} exclude={inputs.codes.map((c) => c.code)} placeholder="Add a procedure — type words or a code…" />
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {inputs.codes.map((c) => (
                    <span key={c.code} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs text-slate-700">
                      <span className="font-mono font-semibold text-brand-navy">{c.code}</span> {c.description}
                      <button onClick={() => setInputs((f) => ({ ...f, codes: f.codes.filter((x) => x.code !== c.code) }))} className="rounded-full p-0.5 hover:bg-slate-200"><XMarkIcon className="h-3.5 w-3.5" /></button>
                    </span>
                  ))}
                  {!inputs.codes.length && suggestions.map((r) => (
                    <button key={r.code} onClick={() => addCode(r)} className="rounded-full border border-dashed border-brand-teal px-2.5 py-0.5 text-xs text-brand-teal hover:bg-teal-50">
                      + <span className="font-mono font-semibold">{r.code}</span> {r.description}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <label className="text-xs font-semibold text-slate-600">Expected time (min)<input type="number" min="0" value={inputs.minutes} onChange={set('minutes')} className={`mt-1 ${field}`} /></label>
                <label className="text-xs font-semibold text-slate-600">BMI<input type="number" min="10" step="0.1" value={inputs.bmi} onChange={set('bmi')} className={`mt-1 ${field}`} /></label>
                <label className="flex items-center gap-2 pt-5 text-sm text-slate-600"><input type="checkbox" checked={inputs.emergency} onChange={set('emergency')} className="rounded border-slate-300" /> Emergency</label>
                <label className="flex items-center gap-2 pt-5 text-sm text-slate-600"><input type="checkbox" checked={inputs.prone} onChange={set('prone')} className="rounded border-slate-300" /> Prone</label>
              </div>

              <div className={estimating ? 'opacity-60' : ''}>
                {estimate ? (
                  <>
                    <Totals practice={estimate.practice_total} scheme={estimate.scheme_total} shortfall={estimate.shortfall} payer={estimate.payer} schemeName={estimate.scheme_name} />
                    <p className="mt-1.5 text-[11px] text-slate-500">
                      Your rate: {estimate.practice.rates.name} · {estimate.scheme ? `${estimate.scheme.rates.name}: ${rands(estimate.scheme.rates.anaesthetic_unit_rate)} per anaesthetic unit` : estimate.payer === 'private' ? 'private patient' : 'no medical aid rates on file'}
                    </p>
                    {estimate.issues.length > 0 && <div className="mt-2 space-y-1">{estimate.issues.map((i) => <Issue key={i.message} issue={i} />)}</div>}
                    <button onClick={() => setShowLines(!showLines)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-teal hover:underline">
                      How the fee is worked out <ChevronDownIcon className={`h-3 w-3 transition ${showLines ? 'rotate-180' : ''}`} />
                    </button>
                    {showLines && (
                      <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                        <TariffLines result={{ ...estimate.practice, issues: [] }} />
                      </div>
                    )}
                  </>
                ) : <p className="text-slate-400">Working out the estimate…</p>}
              </div>

              <label className="block text-xs font-semibold text-slate-600">Note for the patient (optional)
                <textarea rows={2} value={inputs.note} onChange={set('note')} placeholder="e.g. Please call us if you have questions about your shortfall." className={`mt-1 resize-none ${field}`} />
              </label>
              <label className="block text-xs font-semibold text-slate-600">Send to
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="No email on file — you’ll get a link to share" className={`mt-1 ${field}`} />
              </label>
            </>
          )}

          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>

        {mode === 'edit' && (
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3">
            <button onClick={onClose} disabled={!!busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Close</button>
            <button onClick={saveDraft} disabled={!!busy || !inputs.codes.length} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {busy === 'save' ? 'Saving…' : 'Save draft'}
            </button>
            <button onClick={saveAndSend} disabled={!!busy || !inputs.codes.length || errors > 0} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50">
              <PaperAirplaneIcon className="h-4 w-4" /> {busy === 'send' ? 'Sending…' : 'Save & send to patient'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteEditor;
